import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Fast launch → graceful deceleration into the final value
const easeOutExpo = (x) => (x === 1 ? 1 : 1 - Math.pow(2, -10 * x));

// All stats share this duration so they land at the exact same moment
const SHARED_DURATION = 2000;

function useCountUp(rawValue, active, duration = SHARED_DURATION) {
  const numericTarget = parseFloat(String(rawValue).replace(/[^0-9.]/g, '')) || 0;
  const prefix = String(rawValue).startsWith('+') ? '+' : '';
  const suffix = String(rawValue).replace(/^[+-]?[\d,.-]+/, '').trim();

  const [display, setDisplay] = useState(`${prefix}0${suffix}`);
  const frameRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;

    const formatNumber = (n) => n.toLocaleString();
    let start = null;

    function frame(ts) {
      if (start === null) start = ts;
      const elapsed = ts - start;
      const rawProgress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(rawProgress);
      const current = Math.round(numericTarget * easedProgress);
      setDisplay(`${prefix}${formatNumber(current)}${suffix}`);
      if (rawProgress < 1) {
        frameRef.current = requestAnimationFrame(frame);
      }
    }

    frameRef.current = requestAnimationFrame(frame);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [active, rawValue, duration, numericTarget, prefix, suffix]);

  return active ? display : `${prefix}0${suffix}`;
}

const StatItem = ({ value, label, active, index }) => {
  const animated = useCountUp(value, active);

  return (
    <div
      className="text-center py-6 sm:py-4 px-4 group transition-transform duration-300 hover:scale-105"
      style={{ transitionDelay: `${index * 0.12}s` }}
    >
      <div className="font-ibm text-4xl sm:text-5xl md:text-6xl font-bold text-primary-dark mb-2 tracking-tight transition-colors duration-300">
        {animated}
      </div>
      <p className="font-almarai text-base sm:text-lg text-body-muted font-medium">
        {label}
      </p>
    </div>
  );
};

const Stats = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const observe = useScrollAnimation();
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const statsList = [
    { value: t.growthRateValue || '20x', label: t.growthRate },
    { value: t.salesGrowthValue || '+500%', label: t.salesGrowth },
    { value: t.trustedMerchantsValue || '+2,000', label: t.trustedMerchants },
  ];

  return (
    <section className="py-16 md:py-24 bg-white border-b border-edge-light">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl lg:max-w-6xl mx-auto mb-10 md:mb-14">
          <span
            ref={observe}
            className="animate-fade-up inline-flex items-center justify-center px-6 py-2 rounded-full bg-badge text-primary-dark text-sm sm:text-base font-bold font-almarai mb-4"
          >
            {t.statsTitle}
          </span>
          <h2
            ref={observe}
            className="animate-fade-up font-ibm text-[26px] md:text-[36px] font-bold text-heading mb-4 leading-tight"
            style={{ transitionDelay: '0.1s' }}
          >
            {t.statsSubtitle}
          </h2>
          <p
            ref={observe}
            className="animate-fade-up font-almarai text-base md:text-lg text-body-muted leading-relaxed lg:whitespace-nowrap"
            style={{ transitionDelay: '0.2s' }}
          >
            {t.statsDescription}
          </p>
        </div>

        {/* Stats Card Container */}
        <div
          ref={(el) => {
            containerRef.current = el;
            observe(el);
          }}
          className="animate-fade-up max-w-[1200px] mx-auto bg-surface-section rounded-3xl border border-edge-light shadow-sm p-6 sm:p-10"
          style={{ transitionDelay: '0.3s' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 divide-y sm:divide-y-0 sm:divide-x sm:rtl:divide-x-reverse divide-edge-light/80">
            {statsList.map((stat, idx) => (
              <StatItem
                key={idx}
                value={stat.value}
                label={stat.label}
                active={isVisible}
                index={idx}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Stats;